import { useState } from "react";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import TeamPersonnelGroup from "./TeamPersonnelGroup";

/**
 * BulkTeamAssignModal — Assign the same Team Number + Personnel to many
 * migration records at once. Reuses TeamPersonnelGroup so the team/personnel
 * rules (auto-fill from daily composition, occupied-personnel blocking) match
 * the single-row inline popover.
 *
 * The records are updated by looping the same PUT /migration/{id} endpoint the
 * inline popover uses, so backend team-lock + daily-composition sync behave
 * identically per record.
 *
 * Props:
 *   recordIds      — number[] of migration record ids to update
 *   assignmentDate — "YYYY-MM-DD" used for composition + occupancy lookup
 *   onDone         — (updatedRecords[]) => void — all saves succeeded (parent
 *                    merges rows, clears selection, and closes the modal)
 *   onMerge        — (updatedRecords[]) => void — merge partial progress into
 *                    the table without closing (used when a save fails midway)
 *   onClose        — () => void
 */
export default function BulkTeamAssignModal({ recordIds, assignmentDate, onDone, onMerge, onClose }) {
  const { user } = useAuth();
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [teamNumber, setTeamNumber] = useState("");
  const [personnel1Id, setPersonnel1Id] = useState(null);
  const [personnel2Id, setPersonnel2Id] = useState(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const count = recordIds.length;
  const canSave = !!teamNumber && !!personnel1Id && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setError("");
    setSaving(true);
    setProgress(0);
    const updated = [];
    try {
      // Sequential PUTs — keeps daily-composition upserts from racing on the
      // same team/date, and the batch is small (a handful of rows).
      for (const id of recordIds) {
        const { data } = await api.put(`/migration/${id}`, {
          team_number: teamNumber || null,
          personnel_1_id: personnel1Id || null,
          personnel_2_id: personnel2Id || null,
        });
        updated.push(data);
        setProgress(updated.length);
      }
      onDone(updated);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        (typeof detail === "string" ? detail : "Failed to assign team.") +
          (updated.length ? ` (${updated.length} of ${count} saved)` : "")
      );
      // Merge whatever succeeded so the table reflects partial progress, but
      // keep the modal open on the error so it stays visible.
      if (updated.length && onMerge) onMerge(updated);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={saving ? undefined : onClose}>
      <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-[360px] max-w-full" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-700">
            Assign Team to {count} record{count === 1 ? "" : "s"}
          </p>
          <button
            onClick={onClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40"
            title="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <div className="px-4 py-3">
          <TeamPersonnelGroup
            teamMasterId={selectedTeamId}
            personnel1Id={personnel1Id}
            personnel2Id={personnel2Id}
            onTeamChange={(teamId, teamData) => {
              setSelectedTeamId(teamId);
              setTeamNumber(teamData ? teamData.team_number : "");
            }}
            onPersonnel1Change={(id) => setPersonnel1Id(id)}
            onPersonnel2Change={(id) => setPersonnel2Id(id)}
            companyId={user?.company_id}
            assignmentDate={assignmentDate}
            disabled={saving}
          />

          <p className="text-xs text-gray-500 mt-3">
            The same team &amp; personnel will be applied to all {count} selected record{count === 1 ? "" : "s"}.
          </p>
          {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 px-4 pb-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="px-3 py-1.5 text-xs text-white rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? `Assigning… (${progress}/${count})` : `Assign to ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}
